import { useCallback } from "react";
import { SidebarAppSDK } from "@contentful/app-sdk";
import { useSDK } from "@contentful/react-apps-toolkit";
import { ACTION_TYPE, useTranslationContext } from "../context";
import { useDeeplTranslate, useFetchDeeplUsage } from "../services";
import { handleFieldTypesAfterTranslation, handleFieldTypesBeforeTranslation } from "../utils";

export const useTranslation = () => {
    const sdk = useSDK<SidebarAppSDK>();
    const { deeplTranslate } = useDeeplTranslate();
    const { state, dispatch } = useTranslationContext();
    const { translationRequests, formality } = state;
    const { mutate: usageMutation } = useFetchDeeplUsage();

    const resetTranslationRequests = useCallback(() => {
        dispatch({
            type: ACTION_TYPE.SET_TRANSLATION_REQUESTS,
            payload: [],
        });
    }, [dispatch]);

    const setIsTranslating = useCallback(
        (isTranslating: boolean) => {
            dispatch({
                type: ACTION_TYPE.SET_IS_TRANSLATING,
                payload: isTranslating,
            });
        },
        [dispatch]
    );

    const translate = useCallback(async (): Promise<void> => {
        try {
            setIsTranslating(true);

            const promises = translationRequests.flatMap(translationItem =>
                translationItem.to
                    .filter(toLocale => toLocale !== translationItem.from)
                    .map(async toLocale => {
                        const fieldType = sdk.entry.fields[translationItem.fieldId].type;

                        const normalizedBeforeTranslation = await handleFieldTypesBeforeTranslation(
                            translationItem.value,
                            fieldType
                        );

                        const translated = await deeplTranslate(
                            normalizedBeforeTranslation,
                            translationItem.from,
                            toLocale,
                            {
                                formality: translationItem.formality || formality,
                                isHtml: fieldType === "RichText",
                            }
                        );

                        const normalizedAfterTranslation = await handleFieldTypesAfterTranslation(
                            translated,
                            fieldType
                        );

                        await sdk.entry.fields[translationItem.fieldId].setValue(
                            normalizedAfterTranslation,
                            toLocale
                        );
                    })
            );

            await Promise.all(promises);
            usageMutation();
            resetTranslationRequests();
        } catch (error) {
            if (error instanceof Error) {
                sdk.notifier.error(error.message);
            } else {
                sdk.notifier.error("An unknown error has occurred during translation.");
            }
        } finally {
            setIsTranslating(false);
        }
    }, [
        deeplTranslate,
        formality,
        resetTranslationRequests,
        sdk,
        setIsTranslating,
        translationRequests,
        usageMutation,
    ]);

    return { translate };
};
